import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  HStack,
  IconButton,
  Image,
  SimpleGrid,
  Text,
  VStack
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FaChevronRight, FaSignOutAlt, FaUser } from 'react-icons/fa';
import { API_BASE, resolveMediaUrl } from '../api';
import PageLayout from './PageLayout';

const MotionBox = motion(Box);

const resolveIconUrl = (icone) => {
  if (!icone) return '';
  if (icone.startsWith('media:')) {
    return resolveMediaUrl(icone.replace('media:', ''));
  }
  if (icone.startsWith('http') || icone.startsWith('/')) {
    return icone;
  }
  return `/icones/${icone}`;
};

const Home = ({ user, setUser }) => {
  const [pages, setPages] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API_BASE}/paginas?status=published&id_instituicao=1&parent_id=root`)
      .then((response) => response.json())
      .then((data) => {
        const result = data.result || [];
        setPages(
          result.filter((page) => {
            if (page.parent_id === null || page.parent_id === undefined) return true;
            return Number(page.parent_id) === 0;
          })
        );
      })
      .catch(() => setPages([]));
  }, [navigate]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  const handleLogout = () => {
    sessionStorage.removeItem('user');
    if (setUser) {
      setUser(null);
    }
    navigate('/login');
  };

  const handleClick = (page) => {
    if (page.tipo === 'route' && page.rota) {
      navigate(page.rota);
      return;
    }
    if (page.rota === '/perfil') {
      navigate('/perfil');
      return;
    }
    if (page.slug) {
      navigate(`/p/${page.slug}`);
    }
  };

  return (
    <PageLayout accent="#93C8E6" background="#EEF3F7" watermark="/maconaria.png">
      <Box
        minH="100dvh"
        position="relative"
        px={{ base: 4, md: 8 }}
        py={8}
        pb={{ base: 10, md: 12 }}
        overflowX="hidden"
        overflowY="auto"
        sx={{ WebkitOverflowScrolling: 'touch' }}
        touchAction="pan-y"
      >
      <Box maxW="1200px" mx="auto" width="100%" position="relative" zIndex={1}>
        <Flex
          align={{ base: 'flex-start', md: 'center' }}
          justify="space-between"
          direction={{ base: 'column', md: 'row' }}
          gap={{ base: 3, md: 0 }}
          mb={6}
        >
          <IconButton
            aria-label="Sair"
            icon={<FaSignOutAlt />}
            onClick={handleLogout}
            bg="white"
            color="gray.600"
            borderRadius="full"
            boxShadow="0 10px 26px rgba(0,0,0,0.15)"
            _hover={{ bg: 'gray.100' }}
            size="md"
            w="48px"
            h="48px"
            zIndex={2}
          />
       
          <HStack spacing={4}>
            <Box>
              <Text
                fontSize={{ base: 'lg', md: 'lg' }}
                fontFamily="Marcellus"
                fontWeight="600"
                letterSpacing="0.04em"
                color="gray.800"
              >
                Menu principal
              </Text>
            </Box>
          </HStack>
          <Box width="48px" height="48px" />
        </Flex>

        <Flex
          align="center"
          justify="space-between"
          direction="row"
          gap={4}
          flexWrap="wrap"
          mb={8}
        >
          <Box
            bg="white"
            borderRadius="24px"
            p={{ base: 4, md: 6 }}
            boxShadow="0 18px 40px rgba(15,23,42,0.12)"
            width={{ base: '100%', md: '100%' }}
          >
            <Flex
              align={{ base: 'flex-start', md: 'center' }}
              justify="space-between"
              gap={4}
              flexWrap="wrap"
            >
              <Box flex="1" minW="0" textAlign="center">
                <Text fontSize="sm" color="gray.500" letterSpacing="0.12em" mb={2}>
                  Seja Bem-vindo
                </Text>
                <Box
                  bg="#93C8E6"
                  borderRadius="20px"
                  p={2}
                  boxShadow="0 12px 26px rgba(15,23,42,0.18)"
                  display="inline-flex"
                >
                  <Image
                    src={user ? `${API_BASE}/Fotos/${user.id_pessoa}.png` : '/icones/logo.png'}
                    alt={user ? user.nome : 'Usuario'}
                    boxSize={{ base: '80px', md: '88px' }}
                    borderRadius="20px"
                    objectFit="cover"
                    bg="white"
                  />
                </Box>
                <Text
                  mt={3}
                  fontSize={{ base: 'lg', md: 'xl' }}
                  fontWeight="bold"
                  color="gray.800"
                  textAlign="center"
                  lineHeight="short"
                  wordBreak="break-word"
                >
                  {user ? `Ir\u2234 ${user.nome}` : 'Irmão'}
                </Text>
              </Box>
              <HStack spacing={3} alignSelf={{ base: 'flex-start', md: 'center' }}>
                <IconButton
                  aria-label="Meu perfil"
                  icon={<FaUser />}
                  onClick={() => navigate('/perfil')}
                  bg="blue.600"
                  color="white"
                  borderRadius="full"
                  boxShadow="0 10px 22px rgba(15,23,42,0.2)"
                  _hover={{ bg: 'blue.500' }}
                />
              </HStack>
            </Flex>
          </Box>
        </Flex>

        <Box position="relative" zIndex={1} width="100%">
          <Text
            fontSize="lg"
            fontFamily="Marcellus"
            fontWeight="600"
            color="gray.900"
            mb={4}
          >
            Acesso rápido
          </Text>
          <SimpleGrid columns={{ base: 2, md: 3, xl: 4 }} spacing={5}>
            {pages.map((page) => (
              <MotionBox
                key={page.id_pagina || page.titulo}
                p={4}
                minH={{ base: '160px', md: '170px' }}
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
                bg="whiteAlpha.900"
                borderRadius="22px"
                boxShadow="0 14px 30px rgba(15,23,42,0.12)"
                whileHover={{ y: -6 }}
                transition="all 0.2s ease"
                touchAction="pan-y"
                cursor="pointer"
                onClick={() => handleClick(page)}
              >
                <VStack align="start" spacing={3}>
                  <Flex align="center" justify="space-between" width="100%">
                    <Box
                      width="58px"
                      height="58px"
                      borderRadius="16px"
                      bg="linear-gradient(135deg, rgba(147,200,230,0.9), rgba(147,200,230,0.35))"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      boxShadow="inset 0 0 0 1px rgba(255,255,255,0.6)"
                    >
                      {page.icone && (
                        <Image
                          src={resolveIconUrl(page.icone)}
                          alt={page.titulo}
                          boxSize="32px"
                          objectFit="contain"
                        />
                      )}
                    </Box>
                    <Box color="gray.400">
                      <FaChevronRight />
                    </Box>
                  </Flex>
                  <Box>
                    <Text fontWeight="bold" color="gray.800">{page.titulo}</Text>
                    <Text fontSize="xs" color="gray.600" noOfLines={2}>
                      {page.resumo || 'Abrir pagina'}
                    </Text>
                  </Box>
                </VStack>
              </MotionBox>
            ))}
          </SimpleGrid>
        </Box>
      </Box>
    </Box>
    </PageLayout>
  );
};

export default Home;

